import { Sparkles, ShieldCheck, Zap, MessageSquare } from "lucide-react";
import { useTranslation } from "react-i18next";

const CONTENT = {
  es: {
    eyebrow: "Nuevo · IA integrada",
    title: "Conoce a",
    titleAccent: "NadIA",
    titleTail: "tu asistente de proyecto.",
    lede: "Analiza tu tablero, detecta riesgos y bloqueos, y te dice qué hacer hoy — con respuestas concretas, no divagaciones.",
    questions: [
      "¿Qué está en riesgo esta semana?",
      "¿Qué actividades están bloqueadas?",
      "¿Qué persona necesita atención?",
      "¿Qué cambió desde la última actualización?",
      "¿Qué debo hacer hoy para evitar un retraso?",
      "¿Qué pasará si movemos esta fecha?",
    ],
    guards: [
      { title: "No inventa nada",     desc: "Si faltan fechas o responsables, te lo dice — no adivina." },
      { title: "No modifica solo",    desc: "Sugiere acciones, pero nunca cambia tareas sin tu aprobación." },
      { title: "Solo tu proyecto",    desc: "Responde con la información del tablero al que perteneces. Cero fugas." },
    ],
    mock: {
      user: "¿Qué está en riesgo esta semana?",
      conclusionLabel: "Conclusión",
      conclusion: "Riesgo alto: 3 tarjetas vencen esta semana y 1 está bloqueada esperando decisión.",
      evidenceLabel: "Evidencia",
      evidence: [
        "\"Landing v2\" · vence viernes · sin responsable",
        "\"API pagos\" · assignment_status = blocked",
        "\"Migración DB\" · depende de #217 sin fecha",
      ],
      actionsLabel: "Acciones sugeridas",
      actions: [
        "Asignar responsable a \"Landing v2\".",
        "Desbloquear \"API pagos\" — comentario abierto.",
        "Poner fecha a la tarjeta #217.",
      ],
      typing: "…",
    },
    footer: "IA disponible en cada tablero. Con límites diarios claros por plan.",
  },
  en: {
    eyebrow: "New · Built-in AI",
    title: "Meet",
    titleAccent: "NadIA",
    titleTail: "your project assistant.",
    lede: "Analyzes your board, spots risks and blockers, and tells you what to do today — concrete answers, no fluff.",
    questions: [
      "What is at risk this week?",
      "Which activities are blocked?",
      "Who needs attention?",
      "What changed since the last update?",
      "What should I do today to avoid delays?",
      "What if we move this date?",
    ],
    guards: [
      { title: "Never invents",      desc: "If dates or owners are missing, it says so — never guesses." },
      { title: "Never modifies alone", desc: "Suggests actions, never changes tasks without your OK." },
      { title: "Only your project",   desc: "Answers strictly with the board you're in. Zero leaks." },
    ],
    mock: {
      user: "What is at risk this week?",
      conclusionLabel: "Conclusion",
      conclusion: "High risk: 3 cards are due this week and 1 is blocked waiting on a decision.",
      evidenceLabel: "Evidence",
      evidence: [
        "\"Landing v2\" · due Friday · no owner",
        "\"Payments API\" · assignment_status = blocked",
        "\"DB migration\" · depends on #217 with no date",
      ],
      actionsLabel: "Suggested actions",
      actions: [
        "Assign an owner to \"Landing v2\".",
        "Unblock \"Payments API\" — open comment thread.",
        "Set a due date on card #217.",
      ],
      typing: "…",
    },
    footer: "AI available on every board. Daily limits per plan clearly stated.",
  },
};

export default function NadIASection() {
  const { i18n } = useTranslation();
  const lang = (i18n.language || "es").startsWith("en") ? "en" : "es";
  const t = CONTENT[lang];

  return (
    <section
      className="relative py-24 md:py-32 overflow-hidden"
      data-testid="landing-nadia"
      style={{
        background:
          "radial-gradient(ellipse at 20% 10%, rgba(59,130,246,0.20), transparent 55%)," +
          "radial-gradient(ellipse at 80% 90%, rgba(139,92,246,0.22), transparent 55%)," +
          "linear-gradient(180deg, #050818, #0a0f28 50%, #050818)",
        color: "#e2e8f0",
      }}
    >
      {/* Grid overlay */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.08]"
           style={{
             backgroundImage:
               "linear-gradient(#3b82f6 1px, transparent 1px), linear-gradient(90deg, #3b82f6 1px, transparent 1px)",
             backgroundSize: "48px 48px",
           }} />

      <div className="relative max-w-6xl mx-auto px-6 grid lg:grid-cols-[1.1fr_1fr] gap-14 items-center">
        {/* Copy column */}
        <div>
          <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] font-bold"
                style={{ color: "#93c5fd" }}
                data-testid="landing-nadia-eyebrow">
            <Sparkles className="h-3.5 w-3.5" /> {t.eyebrow}
          </span>

          <h2 className="font-display text-4xl md:text-6xl font-semibold tracking-tight mt-4 leading-[1.02]"
              data-testid="landing-nadia-title">
            {t.title}{" "}
            <span
              className="inline-block bg-clip-text text-transparent"
              style={{
                backgroundImage:
                  "linear-gradient(120deg, #60a5fa 0%, #a78bfa 45%, #f472b6 100%)",
              }}
            >
              {t.titleAccent}
            </span>
            ,<br />{t.titleTail}
          </h2>

          <p className="mt-6 text-lg max-w-xl" style={{ color: "#cbd5e1" }}>
            {t.lede}
          </p>

          {/* Sample questions */}
          <div className="mt-8 flex flex-wrap gap-2" data-testid="landing-nadia-questions">
            {t.questions.map((q, i) => (
              <span
                key={q}
                className="text-xs rounded-full px-3 py-1.5 border border-blue-400/25 bg-white/[0.03] hover:bg-white/[0.08] hover:border-blue-400/50 transition cursor-default"
                style={{ animation: "fade-rise 0.5s ease-out both", animationDelay: `${i * 60}ms` }}
              >
                {q}
              </span>
            ))}
          </div>

          {/* Guards */}
          <div className="mt-10 grid sm:grid-cols-3 gap-4" data-testid="landing-nadia-guards">
            {t.guards.map((g, i) => (
              <div key={g.title} className="rounded-2xl border border-emerald-400/20 bg-emerald-400/[0.06] px-4 py-3">
                <div className="flex items-center gap-2 font-semibold text-sm text-emerald-300">
                  <ShieldCheck className="h-4 w-4" /> {g.title}
                </div>
                <p className="text-xs text-slate-400 mt-1 leading-relaxed">{g.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Mock chat column */}
        <div className="relative">
          {/* Halo */}
          <div className="pointer-events-none absolute -inset-8 rounded-[40px] opacity-40"
               style={{
                 background:
                   "radial-gradient(circle at 50% 50%, rgba(139,92,246,0.35), transparent 60%)",
                 filter: "blur(20px)",
               }} />
          <div
            className="relative rounded-3xl border border-blue-400/25 backdrop-blur-xl shadow-2xl shadow-blue-500/20 overflow-hidden"
            style={{
              background:
                "linear-gradient(180deg, rgba(11,15,35,0.85), rgba(20,10,45,0.85))",
            }}
            data-testid="landing-nadia-mock"
          >
            {/* Header */}
            <div className="px-5 py-4 border-b border-white/10 flex items-center gap-3">
              <div className="h-10 w-10 rounded-full grid place-items-center"
                   style={{ background: "conic-gradient(from 210deg, #3b82f6, #8b5cf6, #ec4899, #3b82f6)" }}>
                <div className="h-8 w-8 rounded-full bg-slate-950 grid place-items-center">
                  <Sparkles className="h-4 w-4 text-blue-300" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-semibold text-lg tracking-tight leading-none">NadIA</div>
                <div className="text-[10px] text-blue-200/70 mt-0.5">
                  {lang === "es" ? "Analizando tu tablero…" : "Analyzing your board…"}
                </div>
              </div>
              <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
            </div>

            {/* Messages */}
            <div className="px-5 py-5 space-y-4">
              {/* User bubble */}
              <div className="flex justify-end">
                <div className="max-w-[80%] rounded-2xl rounded-br-md px-4 py-2.5 text-sm bg-blue-600 text-white">
                  {t.mock.user}
                </div>
              </div>

              {/* Assistant bubble */}
              <div className="flex justify-start">
                <div className="max-w-[92%] rounded-2xl rounded-bl-md px-4 py-3 text-sm bg-white/[0.06] border border-blue-400/15 text-slate-100">
                  <div className="font-semibold text-blue-300 uppercase tracking-wider text-[10px] mb-1">
                    {t.mock.conclusionLabel}
                  </div>
                  <p className="leading-relaxed">{t.mock.conclusion}</p>

                  <div className="font-semibold text-blue-300 uppercase tracking-wider text-[10px] mt-4 mb-1">
                    {t.mock.evidenceLabel}
                  </div>
                  <ul className="space-y-1 text-xs text-slate-300">
                    {t.mock.evidence.map((e, i) => (
                      <li key={i}>• {e}</li>
                    ))}
                  </ul>

                  <div className="font-semibold text-blue-300 uppercase tracking-wider text-[10px] mt-4 mb-1">
                    {t.mock.actionsLabel}
                  </div>
                  <ol className="space-y-1 text-xs text-slate-300 list-decimal ml-4">
                    {t.mock.actions.map((a, i) => (
                      <li key={i}>{a}</li>
                    ))}
                  </ol>
                  <span className="inline-block w-1.5 h-3 mt-2 bg-blue-400 animate-pulse rounded-sm" />
                </div>
              </div>
            </div>

            {/* Composer preview */}
            <div className="border-t border-white/10 px-4 py-3 flex items-center gap-2">
              <div className="flex-1 rounded-xl bg-white/[0.05] border border-white/10 px-3 py-2 text-xs text-blue-200/40 italic">
                {lang === "es" ? "Pregúntame sobre este proyecto…" : "Ask me about this project…"}
              </div>
              <div className="rounded-xl grid place-items-center h-8 w-8 shadow-lg shadow-blue-500/40"
                   style={{ background: "linear-gradient(135deg, #3b82f6, #8b5cf6)" }}>
                <Zap className="h-3.5 w-3.5 text-white" />
              </div>
            </div>
          </div>

          {/* Small floating chip */}
          <div className="absolute -bottom-4 -left-3 rounded-full bg-slate-900 border border-blue-400/30 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-blue-300 shadow-lg shadow-blue-500/30 hidden md:flex items-center gap-1.5">
            <MessageSquare className="h-3 w-3" /> {lang === "es" ? "Streaming en vivo" : "Live streaming"}
          </div>
        </div>
      </div>

      <p className="relative max-w-6xl mx-auto px-6 text-center text-xs text-blue-200/60 mt-14">
        {t.footer}
      </p>
    </section>
  );
}
